import type { ProgressData } from '@dcs/contracts';
import type { AnsweredEntry, BookmarkEntry, Progress } from '../ports/progress.ts';

/**
 * Pure merge of study progress between two devices (F6 AC-2.3). No clock, no I/O: the same two
 * inputs always give the same output.
 *
 * Rules (per question id):
 *  - `done`: the entry with the newest `at` wins. On a tie the wrong answer wins, so the result does
 *    not depend on argument order and a wrong answer is never turned into a right one by a tie.
 *  - `wrong`: a question that is wrong on either side stays wrong unless the merged (newest) answer
 *    is correct AND newer than the wrong answer of that side. Nothing else removes a wrong answer
 *    except the tombstones below. Order: oldest answer first (ties by id), like the local repo.
 *  - `updatedAt`: the larger one.
 *  - Tombstones (optional fields of the contract): `resetAt` ("Làm lại") and `wrongClearedAt`
 *    ("clear the wrong list") take the larger value of the two sides. Every `done` entry with
 *    `at < resetAt` is dropped (and its `wrong` id), and a side's `wrong` id answered at or before
 *    `wrongClearedAt` no longer counts. So a reset or a clear made on one device is not undone by
 *    another device that still holds the old answers; answers made after it are kept.
 *  - `bookmarks` (F8 T-3.5, optional): one mark `{ on, at }` per question, a removal kept as
 *    `on: false` (tombstone). Per question the mark with the newer `at` wins; on a tie `on: true`
 *    wins (order independent; a tie keeps the learner's bookmark rather than losing it). Marks with
 *    `at < resetAt` are dropped like the answers, so "Làm lại" also clears the bookmarks made before
 *    it. Conflict cases: A marks, B never had it → marked; A marks at 10, B unmarks at 20 → unmarked
 *    (the tombstone is newer); B unmarks at 20, A marks again at 30 → marked. A simple union was
 *    rejected: a bookmark removed on one device would come back from the other. Tombstones are never
 *    dropped by the merge itself, only by `trimProgress` when the row is over 16 KB.
 * `unionProgress` is commutative, associative and idempotent (`union(union(a, b), b)` equals
 * `union(a, b)`).
 *
 * `mergeProgress` = `unionProgress` + `trimProgress`: the server stores at most 16 KB per class.
 *
 * Heavy users (documented limit): 16 KB holds about 390 entries of the form
 * `"<id>":{"ok":true,"at":<13 digits>}`. Past that, the uploaded copy keeps every wrong answer (up to
 * the contract's 1000) and then the newest other answers; the oldest right answers are left out of
 * the server row. The device that has them keeps them locally (its merge result is written
 * untrimmed), so it never loses data, but another device only sees the trimmed set: "done" counts
 * can differ between devices for users with more than ~390 answered questions in one class. A more
 * compact encoding inside v1 was considered and rejected: dropping `at` (e.g. `at: 0`) would make
 * those entries lose every merge and fall under any `resetAt`, which is worse than leaving them out.
 */

/** Same as `PROGRESS_DATA_MAX_BYTES` in `@dcs/contracts` (a test pins them together). */
export const PROGRESS_DATA_MAX_BYTES = 16_384;
/** Same as the `wrong` array cap of `ProgressDataSchema`. */
export const PROGRESS_WRONG_MAX = 1000;
const MAX_EPOCH_MS = 8_640_000_000_000_000;
/** `AttemptCodeSchema` of the contract: ids outside it would be a 422, so they are never uploaded. */
const ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,63}$/;

type Entry = ProgressData['done'][string];
type Mark = BookmarkEntry;
type MarkMap = Record<string, Mark>;

/** Tombstones of a class (`resetAt`, `wrongClearedAt`); the sync keeps them next to the record. */
export interface ProgressMarks {
  resetAt?: number;
  wrongClearedAt?: number;
}

/** `ProgressMarks` where a field may also be present as `undefined` (= absent). */
interface LooseMarks {
  resetAt?: number | undefined;
  wrongClearedAt?: number | undefined;
}

export function emptyProgressData(): ProgressData {
  return { v: 1, done: {}, wrong: [], updatedAt: 0 };
}

const byId = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

function clampEpoch(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(Math.max(Math.trunc(value), 0), MAX_EPOCH_MS);
}

/** Larger of two optional times; `undefined` when both are. */
function maxOptional(a: number | undefined, b: number | undefined): number | undefined {
  if (a === undefined) return b;
  if (b === undefined) return a;
  return Math.max(a, b);
}

/** Same map with its keys sorted, or `undefined` when empty (old records keep their JSON). */
function sortedMarks(bookmarks: Readonly<MarkMap> | undefined): MarkMap | undefined {
  if (bookmarks === undefined) return undefined;
  const ids = Object.keys(bookmarks).sort(byId);
  if (ids.length === 0) return undefined;
  const out: MarkMap = {};
  for (const id of ids) {
    const mark = bookmarks[id];
    if (mark) out[id] = { on: mark.on, at: mark.at };
  }
  return out;
}

/**
 * `data` (its `bookmarks` included) with the given tombstones; absent ones and an empty `bookmarks`
 * are left out, so old records keep their JSON.
 */
function withMarks(data: ProgressData, marks: LooseMarks): ProgressData {
  const out: ProgressData = { v: 1, done: data.done, wrong: data.wrong, updatedAt: data.updatedAt };
  if (marks.resetAt !== undefined) out.resetAt = marks.resetAt;
  if (marks.wrongClearedAt !== undefined) out.wrongClearedAt = marks.wrongClearedAt;
  const bookmarks = sortedMarks(data.bookmarks);
  if (bookmarks !== undefined) out.bookmarks = bookmarks;
  return out;
}

/** The tombstones of a record. */
export function marksOf(data: ProgressData): ProgressMarks {
  const marks: ProgressMarks = {};
  if (data.resetAt !== undefined) marks.resetAt = data.resetAt;
  if (data.wrongClearedAt !== undefined) marks.wrongClearedAt = data.wrongClearedAt;
  return marks;
}

function newerEntry(a: Entry, b: Entry): Entry {
  if (a.at !== b.at) return a.at > b.at ? a : b;
  // Same instant: the wrong answer wins (order independent).
  return a.ok ? b : a;
}

/** Newer mark wins; on the same instant the bookmark (`on`) wins, whatever the argument order. */
function newerMark(a: Mark, b: Mark): Mark {
  if (a.at !== b.at) return a.at > b.at ? a : b;
  return a.on ? a : b;
}

/** Per-question union of two bookmark maps; marks older than `resetAt` are dropped. */
function unionMarks(
  a: Readonly<MarkMap> | undefined,
  b: Readonly<MarkMap> | undefined,
  resetAt: number | undefined,
): MarkMap | undefined {
  const out: MarkMap = {};
  const ids = new Set([...Object.keys(a ?? {}), ...Object.keys(b ?? {})]);
  const markOf = (side: Readonly<MarkMap> | undefined, id: string): Mark | undefined => {
    const mark = side !== undefined && Object.hasOwn(side, id) ? side[id] : undefined;
    return mark && (resetAt === undefined || mark.at >= resetAt) ? mark : undefined;
  };
  for (const id of [...ids].sort(byId)) {
    const left = markOf(a, id);
    const right = markOf(b, id);
    const best = left && right ? newerMark(left, right) : (left ?? right);
    if (best) out[id] = { on: best.on, at: best.at };
  }
  return Object.keys(out).length === 0 ? undefined : out;
}

/** `wrong` ordered oldest answer first, ties by id. */
function orderWrong(ids: Iterable<string>, done: ProgressData['done']): string[] {
  return [...ids].sort((a, b) => (done[a]?.at ?? 0) - (done[b]?.at ?? 0) || byId(a, b));
}

/** Union of two progress records without any size limit. */
export function unionProgress(a: ProgressData, b: ProgressData): ProgressData {
  const resetAt = maxOptional(a.resetAt, b.resetAt);
  const wrongClearedAt = maxOptional(a.wrongClearedAt, b.wrongClearedAt);
  const ids = new Set<string>([
    ...Object.keys(a.done),
    ...Object.keys(b.done),
    ...a.wrong,
    ...b.wrong,
  ]);
  /** A side's entry, unless the reset dropped it. */
  const entryOf = (side: ProgressData, id: string): Entry | undefined => {
    const entry = Object.hasOwn(side.done, id) ? side.done[id] : undefined;
    return entry && (resetAt === undefined || entry.at >= resetAt) ? entry : undefined;
  };
  const done: Record<string, Entry> = {};
  for (const id of [...ids].sort(byId)) {
    const left = entryOf(a, id);
    const right = entryOf(b, id);
    const best = left && right ? newerEntry(left, right) : (left ?? right);
    if (best) done[id] = { ok: best.ok, at: best.at };
  }
  const stillWrong = (side: ProgressData, id: string): boolean => {
    if (!side.wrong.includes(id)) return false;
    // When this side answered it wrongly (`0` = unknown: a wrong id without an entry).
    const wrongAt = Object.hasOwn(side.done, id) ? (side.done[id]?.at ?? 0) : 0;
    if (resetAt !== undefined && wrongAt < resetAt) return false;
    if (wrongClearedAt !== undefined && wrongAt <= wrongClearedAt) return false;
    const final = done[id];
    if (!final) return true;
    return !(final.ok && final.at > wrongAt);
  };
  const wrong = orderWrong(
    [...ids].filter((id) => stillWrong(a, id) || stillWrong(b, id)),
    done,
  );
  const union: ProgressData = { v: 1, done, wrong, updatedAt: Math.max(a.updatedAt, b.updatedAt) };
  const bookmarks = unionMarks(a.bookmarks, b.bookmarks, resetAt);
  if (bookmarks !== undefined) union.bookmarks = bookmarks;
  return withMarks(union, { resetAt, wrongClearedAt });
}

/**
 * Clock-skew guard for an upload: no time in `data` may be later than `maxAt` (the sync passes its
 * clock + 5 min). A device whose clock runs ahead would otherwise win every merge with its answers,
 * or drop later answers of the other devices with a `resetAt` in the future.
 */
export function capProgressTimes(data: ProgressData, maxAt: number): ProgressData {
  const cap = (value: number): number => Math.min(value, Math.max(maxAt, 0));
  const done: Record<string, Entry> = {};
  for (const id of Object.keys(data.done)) {
    const entry = data.done[id];
    if (entry) done[id] = { ok: entry.ok, at: cap(entry.at) };
  }
  const capped: ProgressData = { v: 1, done, wrong: data.wrong, updatedAt: cap(data.updatedAt) };
  if (data.bookmarks !== undefined) {
    const bookmarks: MarkMap = {};
    for (const [id, mark] of Object.entries(data.bookmarks)) {
      bookmarks[id] = { on: mark.on, at: cap(mark.at) };
    }
    capped.bookmarks = bookmarks;
  }
  return withMarks(capped, {
    resetAt: data.resetAt === undefined ? undefined : cap(data.resetAt),
    wrongClearedAt: data.wrongClearedAt === undefined ? undefined : cap(data.wrongClearedAt),
  });
}

const sizeOf = (data: ProgressData): number => JSON.stringify(data).length;

/** One thing `trimProgress` can keep: an answer (wrong or not) or a bookmark mark. */
interface TrimItem {
  readonly kind: 'answer' | 'mark';
  readonly id: string;
}

/**
 * Makes `data` fit the contract (≤ 16 KB, ≤ 1000 wrong). Ids are ASCII (contract pattern), so the
 * JSON length is the byte length. Keeps, in this order: the wrong answers (newest first), the
 * bookmarks that are on (newest first), the other answers (newest first), then the bookmark
 * tombstones (newest first); the cut is the longest prefix of that order that fits, so it is
 * deterministic and a second trim of the result changes nothing. So the bookmark tombstones go
 * first, then the oldest right answers, then the oldest bookmarks. Tombstones of the class
 * (`resetAt`, `wrongClearedAt`) are always kept. See "Heavy users" above.
 */
export function trimProgress(
  data: ProgressData,
  maxBytes: number = PROGRESS_DATA_MAX_BYTES,
): ProgressData {
  if (data.wrong.length <= PROGRESS_WRONG_MAX && sizeOf(data) <= maxBytes) return data;
  const newestFirst = (a: string, b: string): number =>
    (data.done[b]?.at ?? 0) - (data.done[a]?.at ?? 0) || byId(a, b);
  const marks: Readonly<MarkMap> = data.bookmarks ?? {};
  const newestMarkFirst = (a: string, b: string): number =>
    (marks[b]?.at ?? 0) - (marks[a]?.at ?? 0) || byId(a, b);
  const wrongIds = [...data.wrong].sort(newestFirst).slice(0, PROGRESS_WRONG_MAX);
  const wrongSet = new Set(wrongIds);
  const others = Object.keys(data.done)
    .filter((id) => !wrongSet.has(id))
    .sort(newestFirst);
  const markIds = Object.keys(marks).sort(newestMarkFirst);
  const answer = (id: string): TrimItem => ({ kind: 'answer', id });
  const mark = (id: string): TrimItem => ({ kind: 'mark', id });
  const priority: TrimItem[] = [
    ...wrongIds.map(answer),
    ...markIds.filter((id) => marks[id]?.on === true).map(mark),
    ...others.map(answer),
    ...markIds.filter((id) => marks[id]?.on !== true).map(mark),
  ];
  const classMarks = marksOf(data);

  const take = (count: number): ProgressData => {
    const kept = priority.slice(0, count);
    const keptAnswers = kept.filter((item) => item.kind === 'answer').map((item) => item.id);
    const done: Record<string, Entry> = {};
    for (const id of [...keptAnswers].sort(byId)) {
      const entry = data.done[id];
      if (entry) done[id] = entry;
    }
    const wrong = orderWrong(
      keptAnswers.filter((id) => wrongSet.has(id)),
      done,
    );
    const out: ProgressData = { v: 1, done, wrong, updatedAt: data.updatedAt };
    const keptMarks: MarkMap = {};
    for (const item of kept) {
      const value = item.kind === 'mark' ? marks[item.id] : undefined;
      if (value) keptMarks[item.id] = value;
    }
    out.bookmarks = keptMarks;
    return withMarks(out, classMarks);
  };

  let low = 0;
  let high = priority.length;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if (sizeOf(take(mid)) <= maxBytes) low = mid;
    else high = mid - 1;
  }
  return take(low);
}

/** Merge of a local and a server record, within the 16 KB contract. */
export function mergeProgress(local: ProgressData, server: ProgressData): ProgressData {
  return trimProgress(unionProgress(local, server));
}

/**
 * Local record (plus the class's tombstones, which the sync keeps next to it) to contract shape.
 * Ids the contract would reject are left out (they stay local).
 */
export function toProgressData(progress: Progress, marks: ProgressMarks = {}): ProgressData {
  const done: Record<string, Entry> = {};
  for (const id of Object.keys(progress.answered).sort(byId)) {
    const entry = progress.answered[id];
    if (entry && ID_PATTERN.test(id)) done[id] = { ok: entry.correct, at: clampEpoch(entry.at) };
  }
  const wrong = orderWrong(new Set(progress.wrong.filter((id) => ID_PATTERN.test(id))), done);
  const data: ProgressData = { v: 1, done, wrong, updatedAt: clampEpoch(progress.updatedAt) };
  if (progress.bookmarks !== undefined) {
    const bookmarks: MarkMap = {};
    for (const [id, mark] of Object.entries(progress.bookmarks)) {
      if (ID_PATTERN.test(id)) bookmarks[id] = { on: mark.on, at: clampEpoch(mark.at) };
    }
    data.bookmarks = bookmarks;
  }
  return withMarks(data, {
    resetAt: marks.resetAt === undefined ? undefined : clampEpoch(marks.resetAt),
    wrongClearedAt:
      marks.wrongClearedAt === undefined ? undefined : clampEpoch(marks.wrongClearedAt),
  });
}

/**
 * Contract record to the local shape, keeping `prev` for what the server does not carry: the chosen
 * option (kept only when the entry is the same answer) and ids outside the contract pattern, their
 * answers and bookmarks (unless a reset covers them). The tombstones are not part of the local record (see `marksOf`).
 */
export function fromProgressData(
  licenseClass: string,
  data: ProgressData,
  prev: Progress,
): Progress {
  const resetAt = data.resetAt;
  const answered: Record<string, AnsweredEntry> = {};
  for (const [id, entry] of Object.entries(prev.answered)) {
    if (!ID_PATTERN.test(id) && (resetAt === undefined || entry.at >= resetAt)) {
      answered[id] = entry;
    }
  }
  for (const id of Object.keys(data.done)) {
    const entry = data.done[id];
    if (!entry) continue;
    const before = Object.hasOwn(prev.answered, id) ? prev.answered[id] : undefined;
    const same = before !== undefined && before.correct === entry.ok && before.at === entry.at;
    answered[id] = { lastChoice: same ? before.lastChoice : -1, correct: entry.ok, at: entry.at };
  }
  const foreignWrong = prev.wrong.filter(
    (id) => !ID_PATTERN.test(id) && (resetAt === undefined || Object.hasOwn(answered, id)),
  );
  const progress: Progress = {
    version: 1,
    licenseClass,
    answered,
    wrong: [...foreignWrong, ...data.wrong],
    updatedAt: Math.max(data.updatedAt, 0),
  };
  const bookmarks: MarkMap = {};
  for (const [id, mark] of Object.entries(prev.bookmarks ?? {})) {
    if (!ID_PATTERN.test(id) && (resetAt === undefined || mark.at >= resetAt)) bookmarks[id] = mark;
  }
  for (const [id, mark] of Object.entries(data.bookmarks ?? {})) {
    bookmarks[id] = { on: mark.on, at: mark.at };
  }
  if (Object.keys(bookmarks).length > 0) progress.bookmarks = bookmarks;
  return progress;
}

/** Same content, whatever the key or `wrong` order (to decide "is a PUT needed"). */
export function sameProgressData(a: ProgressData, b: ProgressData): boolean {
  const canon = (d: ProgressData): string =>
    JSON.stringify([
      d.v,
      Object.keys(d.done)
        .sort(byId)
        .map((id) => [id, d.done[id]?.ok, d.done[id]?.at]),
      [...d.wrong].sort(byId),
      d.updatedAt,
      d.resetAt ?? null,
      d.wrongClearedAt ?? null,
      Object.keys(d.bookmarks ?? {})
        .sort(byId)
        .map((id) => [id, d.bookmarks?.[id]?.on, d.bookmarks?.[id]?.at]),
    ]);
  return canon(a) === canon(b);
}
